import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { 
  MembershipTier, 
  TierCapabilities, 
  getCapabilities,
  meetsMinimumTier,
} from '@/types/membership';

interface PrivateContentCounts {
  collections: number;
  items: number;
}

interface MembershipContextType {
  tier: MembershipTier;
  loading: boolean;
  capabilities: TierCapabilities;
  updateTier: (newTier: MembershipTier) => Promise<void>;
  meetsRequirement: (requiredTier: MembershipTier) => boolean;
  refetch: () => Promise<void>;
  getPrivateContentCounts: () => Promise<PrivateContentCounts>;
  makeAllContentPublic: () => Promise<void>;
}

const MembershipContext = createContext<MembershipContextType | undefined>(undefined);

export function MembershipProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [tier, setTier] = useState<MembershipTier>('explorer');
  const [loading, setLoading] = useState(true);

  const fetchMembership = async () => {
    if (!user) {
      setTier('explorer');
      setLoading(false);
      return;
    }

    // Set loading to true at the start of each fetch to prevent
    // stale tier data from being used during the fetch
    setLoading(true);

    try {
      const { data, error } = await supabase
        .from('user_memberships')
        .select('tier')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) {
        console.error('Error fetching membership:', error);
        setTier('explorer');
      } else if (data) {
        setTier(data.tier as MembershipTier);
      } else {
        // No membership record yet - check user_metadata first (survives cross-tab verification),
        // then localStorage as fallback, then default to 'curator' for Founding Member phase
        const metadataTier = user.user_metadata?.selected_tier as MembershipTier | undefined;
        const pendingTier = localStorage.getItem('pendingMembershipTier') as MembershipTier | null;
        const defaultTier = metadataTier || pendingTier || 'curator';
        
        // Create the membership record now that user is authenticated
        // Cast to 'any' to handle the 'curator' tier which may not be in generated types yet
        const { error: insertError } = await supabase
          .from('user_memberships')
          .insert({ user_id: user.id, tier: defaultTier as any });

        if (insertError) {
          console.error('Error creating membership:', insertError);
          setTier('explorer');
        } else {
          setTier(defaultTier);
          // Clear the pending tier
          localStorage.removeItem('pendingMembershipTier');
          
          // Sync new user to Brevo for marketing emails
          try {
            await supabase.functions.invoke('sync-brevo-contact', {
              body: { action: 'opt_in' },
            });
          } catch (e) {
            console.error('Brevo sync error for new user:', e);
          }
        }
      }
    } catch (err) {
      console.error('Error fetching membership:', err);
      setTier('explorer');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMembership();
  }, [user]);

  const updateTier = async (newTier: MembershipTier) => {
    if (!user) return;

    try {
      // Try to update first
      const { data: existingData } = await supabase
        .from('user_memberships')
        .select('id')
        .eq('user_id', user.id)
        .maybeSingle();

      if (existingData) {
        // Update existing record - cast to 'any' to handle 'curator' tier
        const { error } = await supabase
          .from('user_memberships')
          .update({ tier: newTier as any })
          .eq('user_id', user.id);

        if (error) throw error;
      } else {
        // Insert new record - cast to 'any' to handle 'curator' tier
        const { error } = await supabase
          .from('user_memberships')
          .insert({ user_id: user.id, tier: newTier as any });

        if (error) throw error;
      }

      setTier(newTier);
    } catch (err) {
      console.error('Error updating membership:', err);
      throw err;
    }
  };

  const meetsRequirement = (requiredTier: MembershipTier) => {
    return meetsMinimumTier(tier, requiredTier);
  };

  const refetch = async () => {
    await fetchMembership();
  };

  const getPrivateContentCounts = async (): Promise<PrivateContentCounts> => {
    if (!user) return { collections: 0, items: 0 };

    try {
      // Get count of private collections
      const { count: collectionCount } = await supabase
        .from('collections')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', user.id)
        .eq('is_public', false);

      // Get count of private items in user's collections
      const { data: userCollections } = await supabase
        .from('collections')
        .select('id')
        .eq('user_id', user.id);

      let itemCount = 0;
      if (userCollections && userCollections.length > 0) {
        const collectionIds = userCollections.map(c => c.id);
        const { count } = await supabase
          .from('collection_items')
          .select('*', { count: 'exact', head: true })
          .in('collection_id', collectionIds)
          .eq('is_public', false);
        itemCount = count || 0;
      }

      return {
        collections: collectionCount || 0,
        items: itemCount,
      };
    } catch (err) {
      console.error('Error getting private content counts:', err);
      return { collections: 0, items: 0 };
    }
  };

  const makeAllContentPublic = async (): Promise<void> => {
    if (!user) return;

    try {
      // Update all private collections to public
      await supabase
        .from('collections')
        .update({ is_public: true })
        .eq('user_id', user.id)
        .eq('is_public', false);

      // Get user's collection IDs
      const { data: userCollections } = await supabase
        .from('collections')
        .select('id')
        .eq('user_id', user.id);

      if (userCollections && userCollections.length > 0) {
        const collectionIds = userCollections.map(c => c.id);
        
        // Update all private items to public
        await supabase
          .from('collection_items')
          .update({ is_public: true })
          .in('collection_id', collectionIds)
          .eq('is_public', false);
      }
    } catch (err) {
      console.error('Error making content public:', err);
      throw err;
    }
  };

  return (
    <MembershipContext.Provider
      value={{
        tier,
        loading,
        capabilities: getCapabilities(tier),
        updateTier,
        meetsRequirement,
        refetch,
        getPrivateContentCounts,
        makeAllContentPublic,
      }}
    >
      {children}
    </MembershipContext.Provider>
  );
}

export function useMembership() {
  const context = useContext(MembershipContext);
  if (context === undefined) {
    // During HMR, context can temporarily be undefined
    // Return a safe default that triggers loading state
    console.warn('useMembership: MembershipContext not found, using fallback. This may happen during HMR.');
    return {
      tier: 'explorer' as const,
      loading: true,
      capabilities: getCapabilities('explorer'),
      updateTier: async () => {},
      meetsRequirement: () => false,
      refetch: async () => {},
      getPrivateContentCounts: async () => ({ collections: 0, items: 0 }),
      makeAllContentPublic: async () => {},
    };
  }
  return context;
}
